import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';
import type { IconColor, IconName } from '../../icons/types';

export type BreadcrumbItem = {
  disabled?: boolean;
  href?: string;
  icon?: IconName;
  iconColor?: IconColor;
  /** Marks the current page. Defaults to the last item; set `false` on the last item to keep it a link. */
  isCurrent?: boolean;
  /** Decorative content before the label, such as an avatar. */
  leadingContent?: ReactNode;
  text: string;
};

export type ResolvedBreadcrumbItem = Omit<BreadcrumbItem, 'isCurrent'> & {
  isCurrent: boolean;
  isDisabled: boolean;
  isInternalLink: boolean;
  isLast: boolean;
  isLink: boolean;
  key: string;
};

type UseBreadcrumbsParams = {
  ariaLabel?: string;
  items: BreadcrumbItem[];
};

type UseBreadcrumbsReturn = {
  resolvedAriaLabel: string;
  resolvedItems: ResolvedBreadcrumbItem[];
};

function useBreadcrumbs({ ariaLabel, items }: UseBreadcrumbsParams): UseBreadcrumbsReturn {
  const { t } = useTranslation();

  const resolvedAriaLabel = ariaLabel ?? t('common:BreadcrumbNavLabel', { defaultValue: 'Breadcrumb' });

  const resolvedItems: ResolvedBreadcrumbItem[] = items.map((item, index) => {
    const isLast = index === items.length - 1;
    const isDisabled = item.disabled === true;
    const isCurrent = item.isCurrent ?? isLast;
    const isLink = !isDisabled && !isCurrent && Boolean(item.href);
    const isInternalLink = isLink && Boolean(item.href?.startsWith('/'));

    return {
      ...item,
      isCurrent,
      isDisabled,
      isInternalLink,
      isLast,
      isLink,
      key: `${index}-${item.text}`,
    };
  });

  return { resolvedAriaLabel, resolvedItems };
}

export default useBreadcrumbs;
